import {PropsWithChildren, useEffect, useMemo} from "react";
import {ApolloClient, ApolloProvider, concat, createHttpLink, InMemoryCache, split} from "@apollo/client";
import {useMsal} from "@azure/msal-react";
import {GraphQLWsLink} from "@apollo/client/link/subscriptions";
import {createClient} from "graphql-ws";
import {getMainDefinition} from "@apollo/client/utilities";
import {connectionStatus, Status} from "components/StatusBar/StatusBar";
import {startSubscription} from "providers/SubscriptionService";
import {preload} from "providers/PreloadData";
import {graphqlUri, scope} from "env";
import {useSite} from "hooks/useSite";
import {InteractionRequiredAuthError} from "@azure/msal-browser";
import {setContext} from "@apollo/client/link/context";

const httpLink = createHttpLink({ uri: graphqlUri });
const wsLink = new GraphQLWsLink(createClient({
  url: graphqlUri.replace('http', 'ws'),
  on: {
    opened: () => connectionStatus(Status.Connected),
    closed: () => connectionStatus(Status.Disconnected),
    connecting: () => connectionStatus(Status.Connecting)
  },
  retryWait: retries => new Promise(resolve => setTimeout(() => resolve(),
    retries > 10 ? 30000 : 5000))
}));

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

export const GraphqlProvider = ({children}: PropsWithChildren<{}>) => {
  const { instance } = useMsal();

  const client = useMemo(() => {
    const authMiddleware = setContext(async(_, { headers }) => {
      const account = instance.getAllAccounts()[0];
      if (!account) { return headers; }
      const request = {
        scopes: [scope],
        account: account
      };
      const res = await instance
        .acquireTokenSilent(request)
        .catch(async (error) => {
          if (error instanceof InteractionRequiredAuthError) {
            // fallback to interaction when silent call fails
            return await instance.acquireTokenRedirect(request);
          }
        });
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${res?.accessToken}`
        }
      };
    });

    const link = concat(authMiddleware, splitLink);

    return new ApolloClient({
      link,
      cache: new InMemoryCache()
    });
  }, [instance]);

  useEffect(() => {
    startSubscription(client)
  }, [client]);
  const siteId = useSite();
  useEffect(() => {
    preload(client, siteId);
  }, [client, siteId])

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}